/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import { useEffect, useState } from "react";
import { Switch, Route, useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Auth, Hub } from "aws-amplify";
import { Layout } from "../_metronic/layout";
import BasePage from "./BasePage";
import { AuthPage } from "./modules/Auth";
import ErrorsPage from "./modules/ErrorsExamples/ErrorsPage";
import useUserData from "./modules/Auth/getAuthUserData";
import useAdobeDataLayer from "../app/sustell_15/analytics/adobeDataLayer";
import { usePrevLocation } from "../app/sustell_15/helpers/previousLocation";
import featureFlags from "./sustell_15/utils/featureFlags";
import { NotificationProvider } from "./sustell_15/context/NotificationContext";

const NotificationWrapper = ({ children }) =>
  featureFlags.notifications ? (
    <NotificationProvider>{children}</NotificationProvider>
  ) : (
    <>{children}</>
  );

export const Routes = () => {
  const userData = useUserData();
  const isAuthorized = userData !== null;
  const { historyChangeEvent } = useAdobeDataLayer();
  const location = useLocation();
  const prevLocation = usePrevLocation(location);
  const redirectUrl = prevLocation.pathname;
  const selectedCustomer = useSelector((state) => state.selectedCustomer);
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const loadingDefaultCustomer =
    isAuthenticated && !selectedCustomer?.value?.id;
  const loggedInThroughV2 =
    loadingDefaultCustomer && selectedCustomer?.loading === false;

  if (featureFlags.authentication && loggedInThroughV2) {
    Hub.dispatch("auth", {
      event: "signIn",
    });
  }

  useEffect(() => {
    let isMounted = true;

    async function checkAuth() {
      try {
        await Auth.currentSession();
        if (isMounted) setIsAuthenticated(true);
      } catch {
        if (isMounted) setIsAuthenticated(false);
      }
    }

    checkAuth();

    return () => {
      isMounted = false;
    };
  }, [isAuthorized]);

  localStorage.setItem("prev_url", redirectUrl);
  localStorage.setItem(
    "selectedCustomer",
    JSON.stringify({
      id: selectedCustomer?.value?.id ?? "",
      name: selectedCustomer?.value?.name ?? "",
    }),
  );

  const history = useHistory();

  useEffect(() => {
    history.listen((location) => {
      historyChangeEvent(location.pathname);
    });
  }, []);

  const redirectLoginV2 = () => {
    window.location.assign(
      `/v2/sign-in?redirect=${window.btoa(
        location.pathname + location.search,
      )}`,
    );
    return null;
  };

  return (
    <Switch>
      {isAuthenticated === null ||
      loadingDefaultCustomer ? null : isAuthenticated && isAuthorized ? (
        <NotificationWrapper>
          <Layout>
            <BasePage />
          </Layout>
        </NotificationWrapper>
      ) : featureFlags.authentication ? (
        <Route
          render={() => {
            redirectLoginV2();
            return null;
          }}
        />
      ) : (
        <Route>
          <AuthPage />
        </Route>
      )}
      <Route path="/error" component={ErrorsPage} />
    </Switch>
  );
};
